import { getClaimDetailsLanguageDisplayTitle } from "../../../../../../utils/planManager/planConfigurationUtils.js";

/**
 * RBP section — yes/no radios and gated language blocks.
 *
 * @param {object} props
 * @param {object} props.form
 * @param {boolean} props.isRbpYes
 * @param {Record<string, string>} props.validationErrors
 * @param {(title: string) => void} props.onRbpSelect
 * @param {(fieldKey: string, title: string) => void} props.onRbpLanguageSelect
 * @param {(fieldKey: string, value: string) => void} props.onRbpLanguageTextChange
 * @returns {import('react').JSX.Element}
 */
const RbpSection = ({
    form,
    isRbpYes,
    validationErrors,
    onRbpSelect,
    onRbpLanguageSelect,
    onRbpLanguageTextChange,
}) => {
    const selectedRbp =
        form.rbpOptions.find((option) => option.isSelected)?.title ?? null;

    const renderLanguageBlock = (fieldKey, blockLabel) => {
        const options = form[fieldKey];
        const selectedOption = options.find((option) => option.isSelected);
        const selectedTitle = isRbpYes ? (selectedOption?.title ?? null) : null;
        const isTextDisabled = !isRbpYes || !selectedTitle;

        return (
            <div className="plan-notification-rbp-block">
                <span className="plan-notification-field__label">{blockLabel}</span>
                <div
                    className="plan-notification-rbp-radios"
                    role="radiogroup"
                    aria-label={blockLabel}
                >
                    {options.map((option) => {
                        const optionLabel =
                            fieldKey === "claimDetailsLanguage"
                                ? getClaimDetailsLanguageDisplayTitle(
                                      option.title,
                                  )
                                : option.title;
                        const optionId = `${fieldKey}-${option.title}`;

                        return (
                            <label
                                key={option.title}
                                className={[
                                    "plan-notification-rbp-radios__option",
                                    isRbpYes ? null : "is-disabled",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                                htmlFor={optionId}
                            >
                                <input
                                    id={optionId}
                                    type="radio"
                                    name={fieldKey}
                                    disabled={!isRbpYes}
                                    checked={selectedTitle === option.title}
                                    onChange={() =>
                                        onRbpLanguageSelect(
                                            fieldKey,
                                            option.title,
                                        )
                                    }
                                />
                                <span>{optionLabel}</span>
                            </label>
                        );
                    })}
                </div>
                <textarea
                    aria-label={`${blockLabel} copy`}
                    value={
                        isRbpYes && selectedOption ? selectedOption.value ?? "" : ""
                    }
                    disabled={isTextDisabled}
                    rows={4}
                    onChange={(event) =>
                        onRbpLanguageTextChange(fieldKey, event.target.value)
                    }
                />
                {isRbpYes && selectedOption?.helperText ? (
                    <p className="plan-notification-field__helper">
                        {selectedOption.helperText}
                    </p>
                ) : null}
                {validationErrors[fieldKey] ? (
                    <p className="plan-notification-field__error" role="alert">
                        {validationErrors[fieldKey]}
                    </p>
                ) : null}
            </div>
        );
    };

    return (
        <section className="plan-notification-panel plan-notification-panel--rbp">
            <h2 className="plan-notification-panel__title">RBP</h2>

            <div className="plan-notification-field">
                <span className="plan-notification-field__label">
                    Does this plan include RBP?
                </span>
                <div
                    className="plan-notification-rbp-radios"
                    role="radiogroup"
                    aria-label="RBP inclusion"
                >
                    {form.rbpOptions.map((option) => {
                        const optionId = `rbp-${option.title}`;

                        return (
                            <label
                                key={option.title}
                                className="plan-notification-rbp-radios__option"
                                htmlFor={optionId}
                            >
                                <input
                                    id={optionId}
                                    type="radio"
                                    name="rbp-inclusion"
                                    checked={selectedRbp === option.title}
                                    onChange={() => onRbpSelect(option.title)}
                                />
                                <span>{option.title}</span>
                            </label>
                        );
                    })}
                </div>
                {validationErrors.rbpOptions ? (
                    <p className="plan-notification-field__error" role="alert">
                        {validationErrors.rbpOptions}
                    </p>
                ) : null}
            </div>

            <p className="plan-notification-field__helper">
                RBP language for &quot;New claim details available&quot; communication
            </p>

            {renderLanguageBlock(
                "claimDetailsLanguage",
                "New claim details available",
            )}

            <p className="plan-notification-field__helper">
                This is language specific to a Partnership Plan with a PCP network and
                RBP for specialists. This is customizable language that can be used to
                direct the member as to how to find a specialist for their needs.
            </p>

            <div className="plan-notification-rbp-columns">
                {renderLanguageBlock("selfReferral", "Self referral")}
                {renderLanguageBlock("dependentReferral", "Dependent referral")}
            </div>
        </section>
    );
};

export default RbpSection;
